/**
 * Shared content + tokens for the public (guest) marketing site.
 *
 * Reflects the Website First Edit brief: one shared crashpad in San Francisco
 * with 8 total beds and 1 shared bathroom, Cold-Bed accommodations only, the
 * $100 fully refundable deposit + 24-hour approval rule, and SFO transportation
 * (~20 min car / ~45 min transit). The exact street address, passcode, and
 * cleaning-service payment details stay private to approved renters and are not
 * referenced here.
 */

import {BED_INVENTORY, BedType} from '../../types/domain'

export const BRAND = {
  green: '#039540',
  greenDark: '#026e2f',
  ink: '#1b1b29',
  muted: '#7e8299',
  soft: '#f4f6fa',
}

/**
 * Public contact details. The official business phone, email, and urgent number
 * are owner-to-confirm — shown as "Coming soon" until provided. The exact street
 * address is withheld publicly; only approved renters see it in the portal.
 */
export const CONTACT = {
  publicLocation: 'Downtown (Hayes Valley), San Francisco',
  locationBlurb: 'Downtown (Hayes Valley), San Francisco — convenient transportation to and from SFO.',
  // General neighborhood only — the exact building is withheld until a renter is
  // approved and has paid, so the map preview centers on the area, not the pin.
  mapsQuery: 'Hayes Valley, San Francisco',
  phone: null as string | null, // owner to confirm
  email: null as string | null, // owner to confirm
  reserveNote: 'Reserve-friendly — no traditional hotel checkout time.',
}

/**
 * Contact form submits straight into the Google Form (so responses appear in
 * admin → Integrations → Forms). Field ids come from the form's prefill data.
 */
export const CONTACT_FORM = {
  action:
    'https://docs.google.com/forms/d/e/1FAIpQLSc0RyNagt_n7lBToAewUICOjW6XdtNoM-VoKHzrSaiE6W9vUQ/formResponse',
  fields: {
    name: 'entry.923037654',
    email: 'entry.2026063468',
    message: 'entry.187111736',
  },
}

export const NAV_LINKS = [
  {to: '/', label: 'Home'},
  {to: '/rooms', label: 'Accommodations'},
  {to: '/about', label: 'About'},
  {to: '/faq', label: 'FAQ'},
  {to: '/contact', label: 'Contact'},
]

/** Location selector — SFO is live; others are visible but not reservable. */
export const LOCATIONS = [
  {code: 'SFO', label: 'San Francisco (SFO)', active: true},
  {code: 'LAS', label: 'Las Vegas', active: false},
]

/** Crashpad-wide facts. Total beds are fixed at 8; availability is live. */
export const CRASHPAD = {
  totalBeds: 8,
  bathrooms: 1,
  description:
    'One shared crashpad with 8 total beds, 1 shared bathroom, and shared common areas.',
}

/** The $100 fully refundable deposit + mandatory 24-hour approval messaging. */
export const DEPOSIT = {
  amount: '$100',
  warning:
    'After your $100 fully refundable deposit is received, please allow at least 24 hours for reservation review and approval. Login and access information are not available immediately.',
  short:
    'Allow at least 24 hours after your $100 refundable deposit is received for review and approval.',
}

/** Transportation to/from SFO — approximate, with the required disclaimer. */
export const TRANSPORT = {
  car: {icon: '🚗', label: 'Approx. 20 minutes to/from SFO by car'},
  transit: {icon: '🚆', label: 'Approx. 45 minutes to/from SFO by public transit'},
  disclaimer: 'Actual times vary with traffic and transit schedules.',
}

// ─── Bed categories (derived from the canonical inventory) ────────────────
export interface BedCategory {
  type: BedType
  label: string
  codes: string[]
  price: number
}

const CATEGORY_ORDER: BedType[] = [BedType.TWIN, BedType.FULL, BedType.QUEEN]
const CATEGORY_LABEL: Record<BedType, string> = {
  [BedType.TWIN]: 'Twin',
  [BedType.FULL]: 'Full',
  [BedType.QUEEN]: 'Queen',
}

export const BED_CATEGORIES: BedCategory[] = CATEGORY_ORDER.map((type) => {
  const beds = BED_INVENTORY.filter((b) => b.type === type)
  return {
    type,
    label: CATEGORY_LABEL[type],
    codes: beds.map((b) => b.code),
    price: beds[0]?.pricePerMonth ?? 0,
  }
})

export const AMENITIES = [
  {icon: '🍳', label: 'Fully-equipped kitchen'},
  {icon: '🛋️', label: 'Comfortable lounge'},
  {icon: '🧺', label: 'Washer & dryer'},
  {icon: '🧹', label: 'Weekly cleaning'},
  {icon: '📶', label: 'Fast Wi-Fi'},
  {icon: '🏋️', label: 'Gym access'},
]

export const STEPS = [
  {n: 1, title: 'Review live availability', text: 'Browse the eight beds in the crashpad and see which are open right now.'},
  {n: 2, title: 'Select a bed & verify', text: 'Choose an available bed and submit your details, including a current active crew ID.'},
  {n: 3, title: 'Submit the $100 deposit', text: 'Pay the $100 fully refundable deposit to start your reservation review.'},
  {n: 4, title: 'Allow 24 hours for approval', text: 'After your deposit is received, allow at least 24 hours for review and approval.'},
  {n: 5, title: 'Log in & settle in', text: 'Once approved, your renter portal unlocks the address, passcode, and entry instructions.'},
]

export const WHY = [
  {
    icon: '✈️',
    title: 'Convenient to SFO',
    text: 'Approximately 20 minutes by car or 45 minutes by public transit. Times vary with traffic and transit schedules.',
  },
  {
    icon: '🔒',
    title: 'Verified crew only',
    text: 'Every renter is reviewed and must provide a current active crew ID. Reservations are individual and cannot be shared or transferred.',
  },
  {
    icon: '🛏️',
    title: 'Crew comfort',
    text: 'A clean, professionally managed shared crashpad with weekly cleaning, laundry, gym access, and shared common areas.',
  },
]

export const FAQS = [
  {
    q: 'Who may stay at Crosscheck CrashPad?',
    a: 'Verified pilots, flight attendants, and other approved aviation professionals. A current active crew ID is required.',
  },
  {
    q: 'How many beds are there?',
    a: 'There are 8 total beds in one shared crashpad: 3 Twin beds (1T, 3T, 6T), 3 Full beds (2F, 4F, 7F), and 2 Queen beds (5Q, 8Q).',
  },
  {
    q: 'Are these separate rooms?',
    a: 'No. All Twin, Full, and Queen beds are located within one shared crashpad with one shared bathroom and common areas.',
  },
  {
    q: 'Why can I see a bed that is already booked?',
    a: 'All eight beds remain visible so you can see the full inventory. Reserved or occupied beds are clearly labeled and cannot be selected.',
  },
  {
    q: 'Are all beds Cold-Bed accommodations?',
    a: 'Yes. All accommodations are Cold-Bed options — your own assigned bed billed monthly.',
  },
  {
    q: 'How do I reserve a bed?',
    a: 'Select an available bed, submit your verification, and pay the $100 fully refundable deposit. Allow at least 24 hours after the deposit is received for review and approval.',
  },
  {
    q: 'When can I log in?',
    a: 'Private renter login and access information are activated after the deposit is received and the reservation is approved. Approval requires at least 24 hours.',
  },
  {
    q: 'Is the $100 deposit refundable?',
    a: 'Yes — provided there is no unpaid balance or property damage, your personal key fob is returned to its hook, and the permanent-departure steps are completed.',
  },
  {
    q: 'Is there a checkout time?',
    a: 'There is no traditional hotel checkout time. Crosscheck is reserve-friendly, but renters follow their confirmed reservation dates and permanent-departure procedures.',
  },
  {
    q: 'How do I enter the building?',
    a: 'After approval, the private renter portal displays the current access passcode and entry instructions. Check-in does not require speaking with security.',
  },
  {
    q: 'How far is it from SFO?',
    a: 'Approximately 20 minutes by car or 45 minutes by public transit. Times vary with traffic and service conditions.',
  },
  {
    q: 'How do I pay for sheet washing?',
    // Bed washes are charged to the card on file and go straight to the
    // cleaner's queue — no handles or numbers change hands.
    a: 'Optional bed-sheet washing is $5 per set. Approved renters request it in the renter portal and it’s charged to the card on file — housekeeping takes care of the rest.',
  },
  {
    q: 'What is the refrigerator etiquette?',
    a: 'Label all food with your name and date using the markers provided on or near the refrigerator. The cleaning service may discard expired, spoiled, unlabeled, or abandoned items.',
  },
  {
    q: 'What should I do when permanently leaving?',
    a: 'Remove your belongings and food, clear your bed area, lock the door, and return your personal key fob — the one on the keychain with your miniature boarding pass — to its hook in the hallway where you collected it. Check the blue community gym fob is hung back up too, then complete your departure confirmation in the renter portal.',
  },
  {
    q: 'May I share my reservation or the access passcode?',
    a: 'No. Reservations are assigned to one approved renter and cannot be shared or transferred, and the access passcode is for approved active renters only.',
  },
  {
    q: 'Are guests, smoking, or pets permitted?',
    a: 'Only approved occupants may use the crashpad unless permission is granted. No smoking or vaping. No pets unless a legally required accommodation is approved in advance.',
  },
]
